/* eslint-disable max-lines-per-function */
import React, { ReactNode } from "react";
import { ApolloCache, DefaultContext, FetchResult, MutationFunctionOptions, useMutation } from "@apollo/client";
import { navigate, Router } from "@reach/router";
import { Form, Formik, FormikConfig } from "formik";
import * as yup from "yup";

import OrganizationContext from "pages/eligibility/components/context/organization";
import {
  CreateFormSignature,
  EligibilityFormContext_submission as SubmissionType,
  EligibilityFormContext_submission_form as FormType,
  EligibilityFormContext_submission_form_layouts as FormLayoutType,
  EligibilityFormContext_submission_form_layouts_groups as GroupSectionProps,
  SubmitEligibilityForm,
  SubmitEligibilityFormVariables,
  CreateFormSignatureVariables,
} from "types/gql-types";

import { useHelloSign } from "services/hellosign";
import Settings from "settings";

import { unmarshal } from "pages/eligibility/unmarshal";
import { marshalV2 } from "pages/eligibility/marshal";

import queryCreateSignature from "pages/eligibility/query/create-signature.gql";
import querySubmitForm from "pages/eligibility/query/submit.gql";

import { FormStatus, FormValues } from "pages/eligibility/type";

import FormData from "pages/eligibility/components/context/form-data";
import Upload, { createUploadContext } from "pages/eligibility/components/context/upload";
import ApplicationSuccessful from "pages/eligibility/submitted";
import SaveProgress from "../save-progress";

import { keyFromIdentity } from "../helpers";
import { getInitialValue } from "../fields";
import { generateSectionValidations } from "../../validations";
import { QuestionProps } from "../question";
import AuthenticatedForm from "./form-authenticate";


type InitialValues = Record<string, any>;
// Readable name for the section properties
type FormSectionProps = { submission: SubmissionType };

const addAgeRequirementValidations = (
  form: FormType,
  validations: any,
) => {
  if (!form.ageRequirement || !validations["date_of_birth/applicant"]) return validations;

  const thisYear = new Date().getFullYear();
  const onOrBeforeYear = thisYear - form.ageRequirement;

  const ageTest = {
    name: "age",
    exclusive: true,
    params: { age: form.ageRequirement },
    test: (value: string) => new Date(value).getFullYear() <= onOrBeforeYear,
  };

  if (form.ageRequirementOrDisabilityOverride && validations["disability/applicant"]) {
    validations["date_of_birth/applicant"] = validations["date_of_birth/applicant"].when(["disability/applicant"], {
      is: false,
      then: validations["date_of_birth/applicant"].test({
        ...ageTest,
        message: "Must be ${age} or older OR have a disability to apply to this list",
      }),
      otherwise: validations["date_of_birth/applicant"],
    });
  } else {
    validations["date_of_birth/applicant"] = validations["date_of_birth/applicant"].test({
      ...ageTest,
      message: "Must be ${age} or older to apply to this list",
    });
  }

  return validations;
};

const getQuestionsMap = ({ questions }: FormType) =>
  questions.reduce((acc: InitialValues, question: QuestionProps) =>
    ({ ...acc, [keyFromIdentity(question.identity)]: question }), {});

const getValidationSchema = (form: FormType) => {
  const { layouts: [{ groups }] } = form;

  const questionsMap = getQuestionsMap(form);

  const validations = groups
    .reduce((acc: any, section: GroupSectionProps) => ({
      ...acc,
      ...generateSectionValidations(section, questionsMap),
    }), {});

  return yup.object().shape(addAgeRequirementValidations(form, validations));
};

const getInitialValues = (form: FormType) => {
  const { layouts: [{ groups }] } = form;

  const questionsMap = getQuestionsMap(form);

  return groups.reduce((acc: InitialValues, { questions }: {questions: string[][]}) =>
    questions.reduce((questionsAcc: InitialValues, questionIdentity: string[]) => ({
      ...questionsAcc,
      ...getInitialValue(questionsMap[keyFromIdentity(questionIdentity)], questionsMap),
    }), acc), {});
};
type MarshalResponsesGeneratorProps = {
  createFormSignature: (options?: MutationFunctionOptions<CreateFormSignature, CreateFormSignatureVariables, DefaultContext, ApolloCache<any>>) => Promise<FetchResult<CreateFormSignature>>;
  submissionID: string;
  values: any;
  open: (iframeUrl: string) => Promise<{signatureId: string}>;
  submitForm: (options?: FormOptions) => Promise<FetchResult<SubmitEligibilityForm>>;
  questions: QuestionProps[];
  skipSignature: boolean;
}

const marshalResponsesGenerator = ({
  createFormSignature,
  submissionID: submission,
  values,
  open,
  submitForm,
  questions,
  skipSignature,
}: MarshalResponsesGeneratorProps) => () => {
  // Marshal the form question responses into an array of objects, JSON
  // encoding the question value to be sent over the wire.

  // allow empty values for final form submission
  // eslint-disable-next-line no-undefined
  const answers = marshalV2(questions, values, undefined, true);

  const options = { variables: { submission, answers } };
  const doSubmit = () => submitForm(options)
    .then(() => navigate(`/application/${submission}/complete`));

  if (skipSignature) {
    return doSubmit();
  }

  return createFormSignature(options)
    .then((response) => {
      if (!response.data) {
        return Promise.reject(FormStatus.ServerError);
      }

      return open(response.data.createFormSignature)
        .then(doSubmit);
    });
};

// Since we cannot rely on Formik handling validation errors at this point,
// we'll need to run our own validations against the set of all questions
// in the form.
// Formik supports returning a Promise from the onSubmit handler, so we'll
// validate all the schemas in async and when all those are done, either
// resolve the promise if there are no validation errors or reject the
// promise with a set of all validation error messages.
type FormOptions =
MutationFunctionOptions<SubmitEligibilityForm, SubmitEligibilityFormVariables> | undefined;

type ContextDataProp = {
  submissionID: string;
  presets: any;
  form: FormType;
  layout: FormLayoutType;
}

type SubmitGenProps = {
  createFormSignature: (options: any) => Promise<FetchResult<CreateFormSignature>>;
  open: (iframeUrl: string) => Promise<{signatureId: string;}>;
  submitForm: (options?: FormOptions) => Promise<FetchResult<SubmitEligibilityForm>>;
  contextData: ContextDataProp
}

const onSubmitGenerator = ({
  createFormSignature,
  open,
  submitForm,
  contextData,
}: SubmitGenProps):FormikConfig<FormValues>["onSubmit"] => (values, helpers) => {
  const { submissionID, form } = contextData;
  helpers.setStatus(null);

  const marshalResponses = marshalResponsesGenerator({
    createFormSignature,
    submissionID,
    values,
    open,
    submitForm,
    skipSignature: form.skipSignature,
    questions: form.questions,
  });

  return marshalResponses();
};

const getSubmissionData = (submission: SubmissionType) => {
  const {
    id: submissionID,
    presets,
    form,
  } = submission;

  const organizationId = submission.form.property?.organizationId;

  const { layouts: [layout] } = form;
  if (!layout) throw new Error("Forms must have at least one layout.");
  return { submissionID, organizationId, presets, form, layout };
};

type orgContextProps = { organizationId: string | null, children?: ReactNode };
const OrganizationData: React.FC<orgContextProps> = (props: orgContextProps) => (
  <OrganizationContext.Provider value={{ organizationId: props.organizationId }}>
    {props.children}
  </OrganizationContext.Provider>
);

const FormSection: React.FC<FormSectionProps> = ({ submission }) => {
  const contextData = getSubmissionData(submission);
  if (!contextData) return null;

  const { submissionID, organizationId, presets, form } = contextData;
  const { property } = form;

  const [submitForm] =
    useMutation<SubmitEligibilityForm, SubmitEligibilityFormVariables>(querySubmitForm, { refetchQueries: ["AppliedPropertyFeatures"] });

  const [createFormSignature] =
    useMutation<CreateFormSignature, CreateFormSignatureVariables>(queryCreateSignature);

  const [open] = useHelloSign({
    testMode: Settings.env !== "production",
    clientId: Settings.hellosign_client_id || "",
    metadata: {
      submissionID,
    },
  });

  const onSubmit = onSubmitGenerator({
    createFormSignature,
    open,
    submitForm,
    contextData,
  });

  const initialValues = {
    ...getInitialValues(form),
    ...unmarshal(presets),
  };
  const validationSchema = getValidationSchema(form);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnMount={presets.length > 0}
      enableReinitialize
    >
      <FormData.Provider value={form}>
        <Upload.Provider value={createUploadContext(submissionID)}>
          <SaveProgress submissionID={submissionID}>
            <Form id="full-app-form">
              <OrganizationData organizationId={organizationId ?? null}>
                <Router primary={false}>
                  <AuthenticatedForm path="/" />
                  <AuthenticatedForm path=":section" />
                  <ApplicationSuccessful path="complete" form={form} propertyName={property?.name} />
                </Router>
              </OrganizationData>
            </Form>
          </SaveProgress>
        </Upload.Provider>
      </FormData.Provider>
    </Formik>
  );
};

export default FormSection;
